import {
  Modal,
  Box,
  SpaceBetween,
  Header,
  Button,
  Alert,
} from "@amzn/awsui-components-console";
import {
  FusePlan,
  MigrationTargetStage,
  UpdatePlanCommand,
} from "@amzn/fuse-accounting-central-datastore-client";
import React, { useState } from "react";
import { useJemGen } from "src/context/jem-gen/jemGenContext";

interface LinkRegistrationModalProps {
  visible: boolean;
  plan: FusePlan | null;
  registrationId: string | null;
  onDismiss: () => void;
  onSuccess: () => void;
}

export function LinkRegistrationModal({
  visible,
  plan,
  registrationId,
  onDismiss,
  onSuccess,
}: LinkRegistrationModalProps) {
  const [isLinking, setIsLinking] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { getFacdClient, ardRegionMap, selectedArdDetailed } = useJemGen();
  const selectedArd = selectedArdDetailed?.ard;

  const handleLinkRegistration = async () => {
    if (!plan || !plan.id || !registrationId) {
      setError("Missing plan or registration ID");
      return;
    }

    setIsLinking(true);
    setError(null);

    try {
      if (!selectedArd || !selectedArd.id) {
        throw new Error("No ARD selected");
      }

      const domain = ardRegionMap.get(selectedArd.id);
      if (domain === undefined) {
        throw new Error(`Unknown region for ARD ${selectedArd.id}`);
      }

      const facdClient = await getFacdClient(domain);
      if (!facdClient) {
        throw new Error("No FACD client found for region");
      }

      const updatePlanCommand = new UpdatePlanCommand({
        id: plan.id,
        ardId: plan.ardId || selectedArd.id,
        glLineDescriptionPartnerName: plan.glLineDescriptionPartnerName!,
        fusenEnabled: plan.fusenEnabled ?? false,
        migrationTargetStage:
          plan.migrationTargetStage! as MigrationTargetStage,
        fusenEnableDate: plan.fusenEnableDate || "",
        accountingRegistrationId: registrationId,
      });

      console.log(
        "Starting FACD updatePlan operation to link registration:",
        updatePlanCommand,
      );

      await facdClient.send(updatePlanCommand);

      console.log("UpdatePlan operation completed successfully.");

      onSuccess();
      onDismiss();
    } catch (err: unknown) {
      console.error("An error occurred in linkRegistration:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to link registration to plan",
      );
    } finally {
      setIsLinking(false);
    }
  };

  const handleDismiss = () => {
    setError(null);
    onDismiss();
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      closeAriaLabel="Close modal"
      header={<Header variant="h2">Link Registration to Plan</Header>}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={handleDismiss} disabled={isLinking}>
              Cancel
            </Button>
            <Button
              onClick={handleLinkRegistration}
              variant="primary"
              disabled={isLinking}
              loading={isLinking}
            >
              Confirm
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        {error && (
          <Alert
            type="error"
            dismissible
            onDismiss={() => setError(null)}
            header="Error linking registration"
          >
            {error}
          </Alert>
        )}

        <Box>
          <p>
            Are you sure you want to link the following registration to this
            plan?
          </p>
          <SpaceBetween size="s">
            <Box>
              <strong>Plan ID:</strong> {plan?.id || "N/A"}
            </Box>
            <Box>
              <strong>Registration ID:</strong> {registrationId || "N/A"}
            </Box>
          </SpaceBetween>
        </Box>
      </SpaceBetween>
    </Modal>
  );
}
